/**
 * CarmCameraBinding — lightweight adapter that connects CarmViewService
 * (pure data) to a 3D viewport's camera.
 *
 * Subscribes to CarmViewService events and applies/restores camera state
 * on the viewport. Also orchestrates C-arm edit mode: on edit start it snaps
 * the camera to the nearest valid C-arm pose, on every draft change it
 * recomputes the camera from the angles, and on commit/cancel it finalizes
 * or restores.
 *
 * Not a registered service — instantiated and disposed by the useCarmViews hook.
 */

import type {
    CarmEditChangedPayload,
    CarmEditStartedPayload,
    CarmSelectionChangedPayload,
} from './carm-view-service';
import { CarmViewService } from './carm-view-service';
import { CurrentLocationService } from './current-location-service';
import { ViewportService } from './viewport-service';
import type { ServiceManager } from '../core/service-manager';
import type { Vec3 } from '../domain';
import { clampCarmAngles, computeCameraFromCarmAngles, computeCarmAngles } from '../domain/carm-angles';
import type { CarmAngles } from '../domain/carm-angles';
import { cameraStateFromTransform } from '../domain/carm-view';
import { InteractionService } from '../interaction/interaction-service';
import { CarmRotateTool } from '../tools/camera/carm-rotate-tool';
import { PanTool } from '../tools/camera/pan-tool';
import { RotateTool } from '../tools/camera/rotate-tool';
import { VerticalRotateTool } from '../tools/camera/vertical-rotate-tool';
import { MouseButton } from '../tools/enums';
import { ToolGroupManager } from '../tools/tool-group-manager';
import { ViewportType } from '../viewports/types';
import type { CameraState, IViewport } from '../viewports/types';

export class CarmCameraBinding {
    private readonly carmViewService: CarmViewService;
    private readonly serviceManager: ServiceManager;
    private _savedCameraState: CameraState | null = null;
    /** Viewport the C-arm edit bindings were applied to, while an edit is active. */
    private _editViewportId: string | null = null;
    private readonly unsubscribers: Array<() => void> = [];

    constructor(carmViewService: CarmViewService, serviceManager: ServiceManager) {
        this.carmViewService = carmViewService;
        this.serviceManager = serviceManager;

        this.unsubscribers.push(
            carmViewService.subscribe<CarmSelectionChangedPayload>(
                carmViewService.EVENTS.SELECTION_CHANGED,
                (payload) => this.onSelectionChanged(payload)
            ),
            carmViewService.subscribe<CarmEditStartedPayload>(
                carmViewService.EVENTS.EDIT_STARTED,
                (payload) => this.onEditStarted(payload)
            ),
            carmViewService.subscribe<CarmEditChangedPayload>(
                carmViewService.EVENTS.EDIT_CHANGED,
                (payload) => this.onEditChanged(payload)
            ),
            carmViewService.subscribe(carmViewService.EVENTS.EDIT_COMMIT_REQUESTED, () => this.confirmEdit()),
            carmViewService.subscribe(carmViewService.EVENTS.EDIT_COMMITTED, () => this.onEditCommitted()),
            carmViewService.subscribe(carmViewService.EVENTS.EDIT_CANCELLED, () => this.onEditCancelled())
        );
    }

    // ========================================================================
    // Commit path
    // ========================================================================

    /**
     * Commit the active draft: reads the live camera and hands it to the
     * service. The stored transform is therefore exactly what the user sees.
     * Triggered by EDIT_COMMIT_REQUESTED (React consumers have no viewport).
     */
    private confirmEdit(): void {
        if (!this.carmViewService.getDraft()) return;

        const viewport = this.getViewport3D();
        if (!viewport) return;

        this.carmViewService.commitEdit(viewport.getCameraState());
    }

    // ========================================================================
    // Event handlers
    // ========================================================================

    /**
     * Edit-mode entry: save the live camera, then move to the nearest valid
     * C-arm pose — the stored transform for updates, the current camera
     * retargeted onto the current centerline point for creates, in both
     * cases with the angles clamped into the envelope.
     */
    private onEditStarted(payload: CarmEditStartedPayload): void {
        const viewport = this.getViewport3D();
        if (!viewport) {
            // No camera to orchestrate — don't leave a stuck draft behind
            this.carmViewService.cancelEdit();
            return;
        }

        if (this._savedCameraState === null) {
            this._savedCameraState = viewport.getCameraState();
        }

        let base = viewport.getCameraState();
        let angles: CarmAngles;
        if (payload.target === 'new') {
            angles = computeCarmAngles(base.position, base.focalPoint);
            // Anchor a new view on the current centerline point: translate the
            // whole pose so the viewing distance and angles are preserved
            const target = this.resolveCurrentCenterlineTarget();
            if (target) {
                const { position, focalPoint } = base;
                base = {
                    ...base,
                    focalPoint: target,
                    position: [
                        target[0] + (position[0] - focalPoint[0]),
                        target[1] + (position[1] - focalPoint[1]),
                        target[2] + (position[2] - focalPoint[2]),
                    ],
                };
            }
        } else {
            // beginEdit refuses a numeric target with no view, so it resolves
            const view = this.carmViewService.getViews()[payload.target];
            base = cameraStateFromTransform(view.cameraTransform);
            angles = { craniocaudal: view.craniocaudal, oblique: view.oblique };
        }

        const clamped = clampCarmAngles(angles);
        this.applyAnglesToCamera(clamped, base);

        // Push the (possibly clamped) angles into the draft so the panel
        // shows exactly what the camera shows
        this.carmViewService.setDraftAngles(clamped);

        // Left drag becomes the constrained rotation, wheel is unbound.
        // Remember the viewport so commit/cancel restore the same one.
        this._editViewportId = viewport.id;
        this.applyEditBindings(viewport.id);
    }

    private onEditChanged(payload: CarmEditChangedPayload): void {
        const viewport = this.getViewport3D();
        if (!viewport) return;

        this.applyAnglesToCamera(payload.angles, viewport.getCameraState());
    }

    private onEditCommitted(): void {
        // Mirror the pre-edit-mode semantics: a committed create stays
        // selected, so its restore point is the committed pose; a committed
        // update leaves nothing selected, so there is nothing to restore.
        if (this.carmViewService.getSelectedIndex() !== null) {
            this._savedCameraState = this.getViewport3D()?.getCameraState() ?? null;
        } else {
            this._savedCameraState = null;
        }

        this.restoreEditBindings();
    }

    private onEditCancelled(): void {
        this.restoreCameraState();
        this.restoreEditBindings();
    }

    /**
     * Switch the viewport's bindings to C-arm edit mode:
     * - Left drag: constrained CarmRotateTool replaces the current primary
     *   camera tool (whichever it is — the user may have picked Pan/Zoom).
     *   The toolbar lets the user toggle left drag between CarmRotate and Zoom.
     * - Wheel: VerticalRotate (which would violate the C-arm constraint) is
     *   replaced by CarmRotateTool's oblique (LAO/RAO) scroll
     * - Pan (middle) unbound: panning moves the isocenter, silently changing
     *   the committed transform
     * - Zoom (right) keeps its default binding — it is angle-neutral
     */
    private applyEditBindings(viewportId: string): void {
        const manager = this.getToolGroupManager();
        const group = manager.getToolGroupForViewport(viewportId);
        if (!group) return;

        // The tool must be in the group before it can own the primary button
        group.addTool(CarmRotateTool.toolName);
        manager.setPrimaryToolForViewport(viewportId, CarmRotateTool.toolName);
        // A one-shot tool pending from before the edit must not restore the
        // free camera tool it saved — mid-edit that would let the camera
        // leave the constrained pose. Retarget its restore point.
        this.getInteractionService().remapPreviousPrimaryTool(viewportId, CarmRotateTool.toolName);
        group.removeToolBinding(VerticalRotateTool.toolName, { mouseButton: MouseButton.WHEEL });
        // The wheel binding survives a mid-edit primary switch (Zoom, a
        // measurement) — scrolling keeps adjusting LAO/RAO for the whole draft
        group.addToolBinding(CarmRotateTool.toolName, { mouseButton: MouseButton.WHEEL });
        group.removeToolBinding(PanTool.toolName, { mouseButton: MouseButton.AUXILIARY });
    }

    /**
     * Restore the bindings on the viewport the edit was started on (commit,
     * cancel, or teardown safety net). Inverse of applyEditBindings.
     */
    private restoreEditBindings(): void {
        if (this._editViewportId === null) return;

        const viewportId = this._editViewportId;
        this._editViewportId = null;

        // A one-shot tool still pending (started mid-edit) saved CarmRotate
        // as its restore point; the tool leaves the group below, so restoring
        // it would fail silently and leave no drag tool on the primary
        // button. Retarget the restore to the free rotate it stood in for.
        this.getInteractionService().remapPreviousPrimaryTool(
            viewportId,
            RotateTool.toolName,
            CarmRotateTool.toolName
        );

        const manager = this.getToolGroupManager();
        const group = manager.getToolGroupForViewport(viewportId);
        if (!group) return;

        // "Rotate" was CarmRotate during the edit: if it still owns the
        // primary button, hand the button to the free camera Rotate it stood
        // in for. If the user switched to another tool mid-edit (Zoom, a
        // measurement), CarmRotate was stripped from primary at that moment —
        // leave the user's pick alone.
        const carmRotateOwnsPrimary = group
            .getToolsForBinding(MouseButton.PRIMARY)
            .some((tool) => tool.id === CarmRotateTool.toolName);

        // removeTool deactivates the instance (cancels any pending frame)
        // and drops its bindings (primary and wheel) with it
        group.removeTool(CarmRotateTool.toolName);

        if (carmRotateOwnsPrimary) {
            manager.setPrimaryToolForViewport(viewportId, RotateTool.toolName);
        }

        group.setToolActive(PanTool.toolName, {
            bindings: [{ mouseButton: MouseButton.AUXILIARY }],
        });
        group.setToolActive(VerticalRotateTool.toolName, {
            bindings: [{ mouseButton: MouseButton.WHEEL }],
        });
    }

    private onSelectionChanged(payload: CarmSelectionChangedPayload): void {
        if (payload.selectedIndex !== null && payload.cameraTransform) {
            // Selecting a view: save current camera if first selection
            if (this._savedCameraState === null) {
                this._savedCameraState = this.getViewport3D()?.getCameraState() ?? null;
            }
            this.applyCameraState(cameraStateFromTransform(payload.cameraTransform));
        } else {
            // Deselecting: restore saved camera
            this.restoreCameraState();
        }
    }

    // ========================================================================
    // Viewport helpers
    // ========================================================================

    private getToolGroupManager(): ToolGroupManager {
        return this.serviceManager.getService<ToolGroupManager>(ToolGroupManager.SERVICE_NAME);
    }

    private getInteractionService(): InteractionService {
        return this.serviceManager.getService<InteractionService>(InteractionService.SERVICE_NAME);
    }

    /**
     * World point a newly created view should orbit: the current centerline
     * node when one is resolved, otherwise the raw current world position.
     * Null (no retarget) when the location service is missing or has no
     * location yet.
     */
    private resolveCurrentCenterlineTarget(): Vec3 | null {
        let locationService: CurrentLocationService;
        try {
            locationService = this.serviceManager.getService<CurrentLocationService>(
                CurrentLocationService.SERVICE_NAME
            );
        } catch {
            return null;
        }

        const location = locationService.getLocation();
        if (!location) return null;

        if (location.centerlinePosition) {
            const { centerline, nodeIndex } = location.centerlinePosition;
            return centerline.getNodeAt(nodeIndex).point;
        }
        return location.worldPosition ?? null;
    }

    private getViewport3D(): IViewport | null {
        const viewportService = this.serviceManager.getService<ViewportService>(ViewportService.SERVICE_NAME);
        // Every layout has exactly one 3D viewport, but its id differs per
        // layout ('3d-view', 'main', 'top-right', …) — resolve by type
        const viewport = viewportService.getViewportByType(ViewportType.VOLUME_3D);
        if (!viewport) {
            console.warn('[CarmCameraBinding] No 3D viewport found in the current layout');
        }
        return viewport ?? null;
    }

    /**
     * Recompute the camera pose from C-arm angles, preserving the focal point
     * (isocenter), source-to-target distance, and projection parameters of
     * the given base state.
     */
    private applyAnglesToCamera(angles: CarmAngles, base: CameraState): void {
        const distance = Math.hypot(
            base.position[0] - base.focalPoint[0],
            base.position[1] - base.focalPoint[1],
            base.position[2] - base.focalPoint[2]
        );
        if (distance === 0) {
            console.warn('[CarmCameraBinding] Degenerate camera (zero distance), skipping');
            return;
        }

        const pose = computeCameraFromCarmAngles(angles, base.focalPoint, distance);
        this.applyCameraState({
            ...base,
            position: pose.position,
            focalPoint: pose.focalPoint,
            viewUp: pose.viewUp,
        });
    }

    private applyCameraState(state: CameraState): void {
        const viewport = this.getViewport3D();
        if (!viewport) return;

        try {
            viewport.setCameraState(state);
        } catch (error) {
            console.error('[CarmCameraBinding] Failed to apply camera state:', error);
        }
    }

    private restoreCameraState(): void {
        if (!this._savedCameraState) return;

        const viewport = this.getViewport3D();
        if (!viewport) {
            this._savedCameraState = null;
            return;
        }

        try {
            viewport.setCameraState(this._savedCameraState);
        } catch (error) {
            console.error('[CarmCameraBinding] Failed to restore camera state:', error);
        }

        this._savedCameraState = null;
    }

    // ========================================================================
    // Lifecycle
    // ========================================================================

    dispose(): void {
        // A draft can't outlive its orchestrator — cancel before unsubscribing
        // so the EDIT_CANCELLED handler still restores the camera
        this.carmViewService.cancelEdit();
        this.unsubscribers.forEach((unsub) => unsub());
        this.unsubscribers.length = 0;
        this._savedCameraState = null;
        this._editViewportId = null;
    }
}
